import * as React from "react";

import {
  Button,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: "1.5em",
    width: "100%",
    maxWidth: 1200,
    alignSelf: "center",
    overflowX: "auto",
    padding: "0 1.5em 1.5em 1.5em",
  },
  table: { width: "100%" },
  tableCell: { padding: "8px 16px", border: "1px solid #e0e0e0" },
  tableHeader: { backgroundColor: "#f5f5f5", fontWeight: "bold" },
  sectionTitle: {
    marginTop: "2em",
    marginBottom: "1em",
    color: theme.palette.primary.main,
  },
  noDataMessage: {
    textAlign: "center",
    color: theme.palette.grey[500],
    fontStyle: "italic",
    margin: "2em 0",
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "1em",
    marginBottom: "1.5em",
  },
  statTile: { padding: "1em 1.2em", border: "1px solid #e0e0e0", borderRadius: 8 },
  statValue: {
    fontSize: "1.8rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  statLabel: { fontSize: "0.8rem", color: theme.palette.grey[600], marginTop: "0.2em" },
  statSub: { fontSize: "0.75rem", color: theme.palette.grey[500] },
  warnTile: { borderColor: theme.palette.error.main },
  pager: { display: "flex", alignItems: "center", gap: "1em", padding: "1em 0" },
}));

export interface StatTile {
  value: React.ReactNode;
  label: string;
  sub?: string;
  warn?: boolean;
}

export interface OverviewColumn {
  key: string;
  label: string;
  money?: boolean;
  date?: boolean;
}

export interface OverviewPagination {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export const formatUTCDateTime = (value: string) => {
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  const pad = (num: number) => String(num).padStart(2, "0");
  return `${pad(date.getUTCDate())}/${pad(date.getUTCMonth() + 1)}/${date.getUTCFullYear()}, ${pad(
    date.getUTCHours()
  )}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
};

export const money = (value: any) =>
  value === null || value === undefined ? "-" : `£${Number(value).toFixed(2)}`;

const renderCell = (row: any, col: OverviewColumn) => {
  const value = row[col.key];
  if (value === null || value === undefined) return "-";
  if (col.money) return money(value);
  if (col.date) return formatUTCDateTime(value);
  return value;
};

export const StatPanel = ({
  title,
  tiles,
  valueTiles,
}: {
  title: string;
  tiles: StatTile[];
  valueTiles: StatTile[];
}) => {
  const classes = useStyles();

  const grid = (items: StatTile[], keyPrefix: string) => (
    <div className={classes.statGrid}>
      {items.map((tile) => (
        <div
          key={`${keyPrefix}-${tile.label}`}
          className={`${classes.statTile} ${tile.warn ? classes.warnTile : ""}`}
        >
          <div className={classes.statValue}>{tile.value}</div>
          <div className={classes.statLabel}>{tile.label}</div>
          {tile.sub ? <div className={classes.statSub}>{tile.sub}</div> : null}
        </div>
      ))}
    </div>
  );

  return (
    <Paper className={classes.container}>
      <Typography variant="h6" className={classes.sectionTitle}>
        {title}
      </Typography>
      {grid(tiles, "count")}
      {grid(valueTiles, "value")}
    </Paper>
  );
};

export const OverviewTable = ({
  title,
  columns,
  rows,
  emptyMessage,
  rowKey,
  pagination,
  loading,
  onPageChange,
  onPageSizeChange,
}: {
  title: string;
  columns: OverviewColumn[];
  rows: any[];
  emptyMessage: string;
  rowKey?: (row: any, index: number) => React.Key;
  pagination?: OverviewPagination | null;
  loading?: boolean;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}) => {
  const classes = useStyles();

  return (
    <Paper className={classes.container}>
      <Typography variant="h6" className={classes.sectionTitle}>
        {title}
      </Typography>

      {!rows.length ? (
        <Typography className={classes.noDataMessage}>{emptyMessage}</Typography>
      ) : (
        <>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    className={`${classes.tableCell} ${classes.tableHeader}`}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={rowKey ? rowKey(row, index) : index}>
                  {columns.map((col) => (
                    <TableCell key={col.key} className={classes.tableCell}>
                      {renderCell(row, col)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {pagination && onPageChange ? (
            <div className={classes.pager}>
              <Button
                size="small"
                variant="outlined"
                disabled={loading || pagination.page <= 1}
                onClick={() => onPageChange(pagination.page - 1)}
              >
                Previous
              </Button>
              <Typography variant="body2">
                Page {pagination.page} of {pagination.total_pages || 1} ({pagination.total} total)
              </Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={loading || pagination.page >= pagination.total_pages}
                onClick={() => onPageChange(pagination.page + 1)}
              >
                Next
              </Button>
              {onPageSizeChange ? (
                <TextField
                  select
                  size="small"
                  value={pagination.page_size}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  variant="outlined"
                >
                  {[10, 25, 50, 100].map((size) => (
                    <MenuItem key={size} value={size}>
                      {size} / page
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </Paper>
  );
};
